import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { 
  User, Mail, MapPin, Key, Shield, Calendar, 
  ArrowLeft, RefreshCw, AlertCircle, Copy, Check,
  Lock, Clock
} from 'lucide-react';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log(`Fetching user with ID: ${id}`);
      // Try direct ID endpoint first
      let response;
      try {
        response = await api.get(`/api/admin/users/${id}`);
      } catch (err) {
        console.warn('Direct user fetch failed, trying query param...');
        try {
          response = await api.get(`/api/admin/user?id=${id}`);
        } catch (err2) {
          console.warn('Query param fetch failed, trying list fallback...');
          // Fallback: Fetch all users and find the one with matching ID
          const allUsersRes = await api.get('/api/admin/users');
        const getList = (res) => {
          const d = res.data;
          if (Array.isArray(d)) return d;
          if (d.data && Array.isArray(d.data)) return d.data;
          if (d.users && Array.isArray(d.users)) return d.users;
          if (d.data && d.data.data && Array.isArray(d.data.data)) return d.data.data;
          if (d.data && d.data.users && Array.isArray(d.data.users)) return d.data.users;
          return [];
        };
        const usersList = getList(allUsersRes);
        const foundUser = usersList.find(u => String(u.id) === String(id));
        
        if (foundUser) {
          response = { data: foundUser };
        } else {
          throw new Error('User not found in list');
        }
      }
    }

      const userData = response.data.data || response.data.user || response.data;
      setUser(userData);
    } catch (err) {
      console.error('Final fetch error:', err);
      setError('Failed to fetch user details. The user might not exist or you lack permissions.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading user profiles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Error</h2>
          <p className="text-red-700 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/users')}
            className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Users
          </button>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const InfoCard = ({ icon: Icon, label, value, copyable, fieldName }) => (
    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow group">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-gray-900 font-medium break-all">{value || 'N/A'}</p>
          </div>
        </div>
        {copyable && value && (
          <button 
            onClick={() => copyToClipboard(value, fieldName)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100"
          >
            {copiedField === fieldName ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/users')}
              className="p-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-sm"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900">User Profile</h1>
              <p className="text-gray-500">ID: {user.id} • Detailed view of user account</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 ${
            user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            <span className={`w-2 h-2 rounded-full ${user.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
            {user.status?.toUpperCase() || 'UNKNOWN'}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" /> Basic Information
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCard icon={Mail} label="Email Address" value={user.email} copyable fieldName="email" />
                <InfoCard icon={Shield} label="Account Type" value={user.is_admin ? 'Administrator' : 'User'} />
                <div className="sm:col-span-2">
                  <InfoCard icon={MapPin} label="Wallet Address" value={user.address} copyable fieldName="address" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
                <h2 className="text-lg font-bold text-red-800 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-600" /> Sensitive Security Credentials
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-red-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-red-600 uppercase flex items-center gap-1">
                      <Key className="w-3 h-3" /> Key Phrase
                    </span>
                    <button 
                      onClick={() => copyToClipboard(user.key_phrase_encrypted, 'key_phrase')}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      {copiedField === 'key_phrase' ? 'Copied!' : 'Copy Phrase'}
                    </button>
                  </div>
                  <p className="text-gray-900 font-mono text-sm break-all bg-white p-3 rounded-lg border border-gray-200">
                    {user.key_phrase_encrypted || 'No phrase associated'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-red-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-red-600 uppercase flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Private Key (Encrypted)
                    </span>
                    <button 
                      onClick={() => copyToClipboard(user.private_key_encrypted, 'private_key')}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      {copiedField === 'private_key' ? 'Copied!' : 'Copy Key'}
                    </button>
                  </div>
                  <p className="text-gray-900 font-mono text-sm break-all bg-white p-3 rounded-lg border border-gray-200">
                    {user.private_key_encrypted || 'No private key available'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500" /> Account Timeline
                </h2>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 bg-green-50 text-green-600 rounded-full">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Created On</p>
                    <p className="text-gray-900 font-medium">
                      {user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 bg-blue-50 text-blue-600 rounded-full">
                    <RefreshCw className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Last Updated</p>
                    <p className="text-gray-900 font-medium">
                      {user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 bg-orange-50 text-orange-600 rounded-full">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Token Expires</p>
                    <p className="text-gray-900 font-medium">
                      {user.reset_token_expires_at ? new Date(user.reset_token_expires_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-600 rounded-2xl p-6 text-white shadow-lg shadow-blue-200">
              <h3 className="font-bold mb-2">Admin Note</h3>
              <p className="text-blue-100 text-sm leading-relaxed">
                Sensitive information like private keys and key phrases are shown in their stored form. Handle this data with extreme caution.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
